<script setup lang="ts">
import { computed, StyleValue } from 'vue';

/**
 * Props
 */
type Props = {
  forceRight?: boolean;
  forceCenter?: boolean;
  hasRightBorder?: boolean;
  useResizer?: boolean;
  sticky?: boolean | 'left' | 'right';
  stickyOffset?: number;
  contentPadding?: number;
};

const props = withDefaults(defineProps<Props>(), {
  forceRight: false,
  forceCenter: false,
  hasRightBorder: false,
  useResizer: false,
  sticky: false,
  stickyOffset: 0,
  contentPadding: 16,
});

const emits = defineEmits<{
  (e: 'resizer', event: MouseEvent): void;
}>();
const stickyStyle = computed((): StyleValue => {
  if (props.sticky === 'right') {
    return {
      position: 'sticky',
      right: `${props.stickyOffset}px`,
      zIndex: '1',
    };
  } else if (props.sticky) {
    return {
      position: 'sticky',
      left: `${props.stickyOffset}px`,
      zIndex: '1',
    };
  } else {
    return null;
  }
});
</script>

<template>
  <div
    class="table__header"
    :class="{
      'table__header--right': props.forceRight,
      'table__header--center': props.forceCenter,
      'table__header--border--right': props.hasRightBorder,
    }"
    :style="stickyStyle"
  >
    <div class="table__header__content">
      <slot />
    </div>
    <div v-if="useResizer" class="table__header__resizer" @mousedown="(e: MouseEvent) => {emits('resizer', e)}" />
  </div>
</template>

<style lang="scss" scoped>
.table__header {
  position: relative;
  display: grid;
  align-items: center;
  z-index: 0;

  background-color: $surfaceSecondary;

  &--right {
    text-align: right;
  }

  &--center {
    text-align: center;
  }

  &--sticky-left {
    z-index: 3;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  }

  &--border {
    &--right::after {
      content: '';
      position: absolute;
      top: 4px; // 上の余白
      bottom: 4px; // 下の余白
      right: 0;
      width: 1px;
      background-color: $borderSecondary;
    }
  }

  &__content {
    display: inline-block;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
    text-align: left;
    text-decoration-skip-ink: none;
    white-space: nowrap;

    padding: v-bind('`0 ${props.contentPadding}px`');

    color: $textDefault;
  }

  &__resizer {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    z-index: 5;
    cursor: col-resize;

    &::after {
      content: '';
      position: absolute;
      right: 2px;
      top: 25%;
      bottom: 25%;
      width: 2px;
      border-radius: 1px;
      background-color: $borderSecondary;
      transition: background-color 0.15s ease;
    }

    &:hover::after {
      background-color: $borderActive;
    }
  }
}
</style>
